import { useMutation } from '@apollo/client';
import {
  CountryCode,
  CreateDiscountMutation,
  CreateDiscountMutationVariables,
  DiscountType,
  FixedAmountAllocation,
  ProductCondition,
  UserType,
} from '_graphql/mutation/__generated__/graphql';
import { useAppNavigate, useCurrentUser } from '_graphql/cache/auth';
import { createDiscount } from '_graphql/mutation/discount';
import {
  Avatar,
  DeliveryLocationInput,
  List,
  OfficeButton,
  OfficeHeader,
  RichEditor,
  SelectInput,
  Show,
  TextInput,
  toast,
  UploadArea,
} from 'components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SectionLayout from 'pages/create-product/components/SectionLayout';
import Toggle from 'pages/create-product/components/Toggle';
import { FC, useState } from 'react';
import SelectProductContainer from './select-product';
import _ from 'lodash';
import { useProducts } from 'pages/products/hooks/use-products';
import moment from 'moment';

import UploadProductContainer from './upload-product';

type Props = {};

const CreateDiscountPage: FC<Props> = (props) => {
  const [openProduct, setOpenProduct] = useState<boolean>(false);
  const [openProductUpload, setOpenProductUpload] = useState<boolean>(false);
  const [discountCreation, { loading: creating }] = useMutation<
    CreateDiscountMutation,
    CreateDiscountMutationVariables
  >(createDiscount, {
    refetchQueries: ['GetDiscounts'],
  });

  const currentUser = useCurrentUser();
  const navigate = useAppNavigate();

  const handleCheckboxChange = (event: any) => {
    if (discountInfoForm.values?.products?.includes(event.target.value)) {
      const nextValue = discountInfoForm.values.products.filter(
        (value: any) => value !== event.target.value
      );
      discountInfoForm.setFieldValue('products', nextValue);
    } else {
      discountInfoForm.setFieldValue('products', [
        ...discountInfoForm.values.products,
        event.target.value,
      ]);
    }
  };

  const discountInfoForm = useFormik({
    initialValues: {
      description: '',
      expiry_date: '',
      code: '',
      is_draft: false,
      type: DiscountType.Percentage,
      start_date: '',

      amount: 0,
      created_by: UserType.Supplier,
      fixed_amount_allocation: FixedAmountAllocation.TotalAmount,
      locations: [],
      products: [] as string[],
      locations_tagname: '',
      percentage: 0,
      r_limit: null,
      banner_url: '',
    },
    validationSchema: Yup.object().shape({
      description: Yup.string().required('Description is required'),
      expiry_date: Yup.string().required('Expiry date is required'),
      banner_url: Yup.string()
        .url('Banner url must be a valid url')
        .required('Banner url is required'),
      code: Yup.string().required('Code is required'),
      type: Yup.string()
        .oneOf([
          DiscountType.FixedAmount,
          DiscountType.Percentage,
          DiscountType.FreeDelivery,
        ])
        .required('Type is required'),
      start_date: Yup.string().required('Start date is required'),
      amount: Yup.number().when('type', {
        is: DiscountType.FixedAmount,
        then: (schema) => schema.required('Amount is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      created_by: Yup.string().required('Created by is required'),
      fixed_amount_allocation: Yup.string().required(
        'Fixed amount allocation is required'
      ),
      locations: Yup.array().required('Locations is required'),
      products: Yup.array().required('Products is required'),
      locations_tagname: Yup.string().when('type', {
        is: DiscountType.FreeDelivery,
        then: (schema) => schema.required('Location Tagname is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      percentage: Yup.number().when('type', {
        is: DiscountType.Percentage,
        then: (schema) => schema.required('Percentage is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      r_limit: Yup.string().required('Redemption limit is required'),
    }),
    onSubmit: async (values) => {
      await discountCreation({
        variables: {
          input: {
            type: values.type as DiscountType,
            valid_country: currentUser.supplierSignIn.supplier?.country as CountryCode,
            description: values.description,
            code: values.code,
            is_draft: values.is_draft,
            start_date: moment(values.start_date).format('DD/MM/YYYY'),
            expiry_date: moment(values.expiry_date).format('DD/MM/YYYY'),
            amount: values.type === 'FIXED_AMOUNT' ? values.amount : null,
            locations_tagname:
              values.type === 'FREE_DELIVERY' ? values.locations_tagname : null,
            locations:
              values.type === 'FREE_DELIVERY' ? values.locations : null,
            fixed_amount_allocation:
              values.type === 'FIXED_AMOUNT'
                ? FixedAmountAllocation.TotalAmount
                : null,
            percentage: values.type === 'PERCENTAGE' ? values.percentage : null,
            products: values.products,
            user_id: currentUser?.supplierSignIn?.supplier?.id,
            redemptions_limit: values.r_limit,
            created_by: UserType.Supplier,
            product_condition: ProductCondition.In,
            banner_url: values.banner_url,
          },
        },
      })
        .then(({ data }) => {
          if (data?.createDiscount) {
            toast('Discount created successfully', 'success');
            discountInfoForm.resetForm();
            navigate({
              to: '/discount',
            });
          } else {
            toast('Something went wrong', 'error');
          }
        })
        .catch((err) => {
          toast(err.message, 'error');
        });
    },
  });

  const { products, loading: productLoading } = useProducts({
    variables: {
      pagination: {
        limit: discountInfoForm.values.products.length,
        offset: 0,
      },
      filter: {
        supplier_id: {
          eq: currentUser?.supplierSignIn?.supplier?.id,
        },
        sku: { in: discountInfoForm.values.products },
      },
    },
  });

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <OfficeHeader />
      <div className="w-full overflow-y-auto">
        <form className="w-full py-5">
          <div className=" grid grid-cols-1 md:grid-cols-5">
            {/* left */}
            <div className="w-full col-span-3">
              <SectionLayout title="Discount Information">
                <>
                  <div className="col-span-full">
                    <TextInput
                      id={'code'}
                      label="Code"
                      type="text"
                      description="This serves as a name for the discount"
                      placeholder="eg. WINTERSALE"
                      {...discountInfoForm}
                    />
                  </div>
                  <div className="col-span-full">
                    <SelectInput
                      options={[
                        { label: 'Percentage', value: 'PERCENTAGE' },
                        {
                          label: 'Fixed Amount',
                          value: 'FIXED_AMOUNT',
                        },
                        {
                          label: 'Free Delivery',
                          value: 'FREE_DELIVERY',
                        },
                      ]}
                      id={'type'}
                      label="Discount Type"
                      {...discountInfoForm}
                    />
                  </div>
                  <div className="col-span-full">
                    <Show if={discountInfoForm.values.type === 'PERCENTAGE'}>
                      <TextInput
                        id={'percentage'}
                        label="Percentage"
                        type="number"
                        description="The value must be between 1% - 99%"
                        min={1}
                        placeholder="eg. 20"
                        {...discountInfoForm}
                      />
                    </Show>
                    <Show if={discountInfoForm.values.type === 'FIXED_AMOUNT'}>
                      <div className="space-y-3">
                        <TextInput
                          id={'amount'}
                          label="Amount"
                          type="number"
                          min={1}
                          {...discountInfoForm}
                        />
                      </div>
                    </Show>
                    <Show if={discountInfoForm.values.type === 'FREE_DELIVERY'}>
                      <div className="space-y-3">
                        <TextInput
                          id={'locations_tagname'}
                          label="Location Tagname"
                          placeholder="eg. Accra Environs"
                          type="text"
                          {...discountInfoForm}
                        />

                        <div className="col-span-full">
                          <DeliveryLocationInput form={discountInfoForm} />
                        </div>
                      </div>
                    </Show>
                  </div>

                  <div className="col-span-3">
                    <TextInput
                      type="date"
                      label="Start Date"
                      id="start_date"
                      {...discountInfoForm}
                    />
                  </div>
                  <div className="col-span-3">
                    <TextInput
                      label="Expiry Date"
                      type="date"
                      id={'expiry_date'}
                      {...discountInfoForm}
                    />
                  </div>
                  <div className="col-span-full">
                    <TextInput
                      label="Redemption Limit"
                      type="number"
                      id={'r_limit'}
                      {...discountInfoForm}
                    />
                  </div>
                  <div className="col-span-full">
                    <span>Banner URL</span>
                    <div className="grid gap-6 mt-2">
                      <UploadArea
                        className="aspect-w-6 aspect-h-2 w-full"
                        id={'banner_url'}
                        {...discountInfoForm}
                      />
                    </div>
                  </div>
                  <div className="col-span-full">
                    <RichEditor
                      id={'description'}
                      label="Discount Description"
                      {...discountInfoForm}
                    />
                  </div>
                </>
              </SectionLayout>
            </div>
            {/* right */}
            <div className="md:col-span-2">
              <div className="md:sticky top-8">
                <SectionLayout
                  title={'Product List'}
                  renderActions={() => (
                    <div className="flex space-x-2">
                      <OfficeButton
                        size="sm"
                        onClick={() => setOpenProductUpload(true)}
                      >
                        Upload
                      </OfficeButton>{' '}
                      <OfficeButton
                        size="sm"
                        onClick={() => setOpenProduct(true)}
                      >
                        Add
                      </OfficeButton>
                    </div>
                  )}
                >
                  <div className="col-span-full max-h-96 overflow-auto">
                    {discountInfoForm.values?.products.length > 0 && (
                      <List
                        data={discountInfoForm?.values?.products || []}
                        renderItem={(data) => {
                          const newProduct = products?.find(
                            (prod) => prod?.sku === data
                          );
                          if (newProduct) {
                            return (
                              <div
                                key={newProduct?.id}
                                className="relative flex items-start pb-4 pt-3.5"
                              >
                                <div className="mr-3 flex items-start">
                                  <Avatar
                                    alt={'Product avatar'}
                                    size="sm"
                                    src={newProduct?.avatar}
                                  />
                                </div>
                                <div className="min-w-0 flex-1 capitalize text-sm leading-6">
                                  <label
                                    htmlFor={_.snakeCase(newProduct?.title)}
                                    className="font-medium text-gray-900"
                                  >
                                    {newProduct?.title}
                                  </label>
                                  <p
                                    id="candidates-description"
                                    className="text-gray-500"
                                  >
                                    {newProduct?.sku}
                                  </p>
                                </div>
                                <div className="ml-3 flex h-6 items-center">
                                  <input
                                    type="checkbox"
                                    value={newProduct?.sku}
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                                    onChange={handleCheckboxChange}
                                    checked={discountInfoForm.values?.products?.includes(
                                      newProduct?.sku
                                    )}
                                  />
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div
                              key={data}
                              className="relative flex items-start pb-4 pt-3.5"
                            >
                              <div className="mr-3 flex items-start">
                                <Avatar alt={'Product Avatar'} size="sm" />
                              </div>
                              <div className="min-w-0 flex-1 capitalize text-sm leading-6">
                                <label
                                  htmlFor={_.snakeCase(data)}
                                  className="font-medium text-gray-900"
                                >
                                  Unknown Product
                                </label>
                                <p
                                  id="candidates-description"
                                  className="text-gray-500"
                                >
                                  {data}
                                </p>
                              </div>
                              <div className="ml-3 flex h-6 items-center">
                                <input
                                  type="checkbox"
                                  value={data}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                                  onChange={handleCheckboxChange}
                                  checked={discountInfoForm.values?.products?.includes(
                                    data
                                  )}
                                />
                              </div>
                            </div>
                          );
                        }}
                      />
                    )}
                  </div>
                </SectionLayout>
                <SectionLayout title={'Publish'}>
                  <>
                    <div className="col-span-full">
                      <Toggle
                        label="Visibility"
                        description="Discount are set to draft ie unpublish until set to live."
                        value={discountInfoForm.values.is_draft}
                        setValue={(value) => {
                          discountInfoForm.setFieldValue(
                            'is_draft',
                            value ? true : false
                          );
                        }}
                        display={
                          discountInfoForm.values.is_draft ? 'Live' : 'Draft'
                        }
                      />
                    </div>
                    <div className="col-span-full flex items-center justify-between gap-5">
                      <OfficeButton
                        size="sm"
                        width="auto"
                        variant="minimal"
                        onClick={() => navigate({ to: '/discount' })}
                      >
                        Discard
                      </OfficeButton>
                      <OfficeButton
                        onClick={discountInfoForm.handleSubmit}
                        size="sm"
                        width="auto"
                        disabled={creating}
                        type="submit"
                      >
                        {creating ? 'Saving...' : 'Save'}
                      </OfficeButton>
                    </div>
                  </>
                </SectionLayout>
              </div>
            </div>
          </div>
        </form>
      </div>
      <SelectProductContainer
        open={openProduct}
        setOpen={setOpenProduct}
        form={discountInfoForm}
        handleCheckboxChange={handleCheckboxChange}
      />
      <UploadProductContainer
        open={openProductUpload}
        setOpen={setOpenProductUpload}
        form={discountInfoForm}
        loading={productLoading}
      />
    </main>
  );
};

export default CreateDiscountPage;
